import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  buildDeliveryMapStops,
  DeliveryRouteMap,
} from '../../components/dem/DeliveryRouteMap';
import { Modal } from '../../components/ui/Modal';
import { usePortal } from '../../contexts/PortalContext';
import { useDeliveryArrangementsRealtime } from '../../hooks/useDeliveryArrangementsRealtime';
import { composeDeliveryAddress, demMainCustomerCode } from '../../lib/demCustomers';
import { deliveryReorderPath } from '../../lib/deliveryArrangementPrefill';
import {
  addLalamovePriorityFee,
  cancelLalamoveDelivery,
  changeLalamoveDriver,
  deliveryAssigneeLabel,
  deliveryStatusTone,
  deliveryTypeLabel,
  fetchDeliveryBatch,
  fetchLalamoveDriverDetails,
  fetchLalamoveLiveOrder,
  formatDeliveryMoney,
  formatDeliveryStatusLabel,
  friendlyLalamoveVehicleName,
  isDeliveryStatusCancelled,
  isDeliveryStatusTerminal,
  normalizeDeliveryStatus,
  refreshLalamoveDeliveryStatus,
  updateInternalDeliveryStatus,
  type LalamoveDriverDetails,
  type LalamoveLiveOrder,
} from '../../lib/deliveryArrangements';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import type { DeliveryArrangement } from '../../types/delivery';

function resolveActualFee(
  live: LalamoveLiveOrder | null,
  row: DeliveryArrangement,
): number | null {
  const priority =
    live?.priorityFee != null && live.priorityFee !== ''
      ? Number(live.priorityFee)
      : NaN;
  const liveFee = live?.feeAmount;
  const stored = row.fee_amount;
  const reference = row.reference_fee_amount;

  if (liveFee != null && Number.isFinite(liveFee)) {
    // Tip set but Lalamove still returns base-only total.
    if (
      Number.isFinite(priority) &&
      priority > 0 &&
      reference != null &&
      Math.abs(liveFee - Number(reference)) < 0.01
    ) {
      return Number(reference) + priority;
    }
    return liveFee;
  }

  if (stored != null && Number.isFinite(Number(stored))) {
    if (
      Number.isFinite(priority) &&
      priority > 0 &&
      reference != null &&
      Math.abs(Number(stored) - Number(reference)) < 0.01
    ) {
      return Number(reference) + priority;
    }
    return Number(stored);
  }

  if (reference != null && Number.isFinite(priority) && priority > 0) {
    return Number(reference) + priority;
  }

  return reference != null ? Number(reference) : null;
}

export function DemDeliveriesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { basePath } = usePortal();
  const listBase = basePath ? `${basePath}/deliveries` : '/deliveries';

  const [batchRows, setBatchRows] = useState<DeliveryArrangement[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [live, setLive] = useState<LalamoveLiveOrder | null>(null);
  const [driver, setDriver] = useState<LalamoveDriverDetails | null>(null);
  const [priorityFeeModalOpen, setPriorityFeeModalOpen] = useState(false);
  const [priorityFeeInput, setPriorityFeeInput] = useState('5');

  const row = batchRows[0] ?? null;

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!id) return;
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      let rows = await fetchDeliveryBatch(id);
      const head = rows[0];
      if (head?.fulfillment_type === 'lalamove' && head.lalamove_order_id) {
        try {
          const order = await fetchLalamoveLiveOrder(head.lalamove_order_id);
          setLive(order);
          const current = rows[0];
          if (
            current &&
            order.status &&
            normalizeDeliveryStatus(order.status) !== normalizeDeliveryStatus(current.status)
          ) {
            await refreshLalamoveDeliveryStatus(current.id);
            rows = await fetchDeliveryBatch(id);
          }
          setBatchRows(rows);
          if (order.driverId) {
            try {
              setDriver(
                await fetchLalamoveDriverDetails(head.lalamove_order_id, order.driverId),
              );
            } catch {
              setDriver(null);
            }
          } else {
            setDriver(null);
          }
        } catch {
          if (!silent) setLive(null);
          setBatchRows(rows);
        }
      } else {
        setBatchRows(rows);
        setLive(null);
        setDriver(null);
      }
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load arrangement.');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Webhook updates the DB; Realtime tells the open page to reload (incl. live driver/POD).
  useDeliveryArrangementsRealtime({
    enabled: Boolean(id),
    onChange: () => {
      void load({ silent: true });
    },
  });

  const mapStops = useMemo(() => {
    if (!row) return [];
    return buildDeliveryMapStops(
      {
        address: composeDeliveryAddress(row.pickup_json) || row.pickup_json.address,
        postal: row.pickup_json.postal,
        name: row.pickup_json.name,
      },
      batchRows.map((stopRow) => ({
        key: stopRow.id,
        address:
          composeDeliveryAddress(stopRow.dropoff_json) || stopRow.dropoff_json.address,
        postal: stopRow.dropoff_json.postal,
        name: stopRow.dropoff_json.name,
        demCustomerCode: stopRow.dem_customer_code,
      })),
    );
  }, [row, batchRows]);

  const driverLocation =
    driver?.lat != null && driver?.lng != null
      ? { lat: driver.lat, lng: driver.lng, label: driver.name || 'Driver' }
      : null;

  async function runAction(label: string, fn: () => Promise<void>) {
    if (!row) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await load({ silent: true });
      setNotice(label);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="delivery-page delivery-page--detail">
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading delivery…</p>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="delivery-page delivery-page--detail">
        <p className="empty-state">Delivery arrangement not found.</p>
        <Link to={listBase} className="btn-secondary delivery-page__back">
          Back to list
        </Link>
      </div>
    );
  }

  const multiStop = batchRows.length > 1;
  const statusTone = deliveryStatusTone(live?.status || row.status);
  const statusLabel = formatDeliveryStatusLabel(live?.status || row.status);
  const isLalamove = row.fulfillment_type === 'lalamove';
  const vehicleLabel = row.vehicle_type ? friendlyLalamoveVehicleName(row.vehicle_type) : null;
  const canAct =
    isLalamove && !isDeliveryStatusTerminal(live?.status || row.status);
  const canReorder = isDeliveryStatusCancelled(live?.status || row.status);
  const reorderPath = deliveryReorderPath(listBase, row.id);
  const hasDriver = Boolean(live?.driverId);
  // Lalamove stops: index 0 = pickup, index 1..n = dropoffs (stop_sequence).
  const liveStops = live?.stops ?? [];
  const driverNotesEntries = batchRows
    .map((stop) => ({
      sequence: stop.stop_sequence,
      notes: stop.dropoff_json.notesToDriver?.trim() || '',
    }))
    .filter((entry) => entry.notes);
  const actualFee = resolveActualFee(live, row);
  const priorityFeeDisplay =
    live?.priorityFee && Number(live.priorityFee) > 0 ? live.priorityFee : null;

  return (
    <div className="delivery-page delivery-page--detail delivery-page--floating">
      <Link to={listBase} className="btn-secondary delivery-page__back">
        Back to list
      </Link>

      {notice ? <div className="alert alert-success">{notice}</div> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className={`delivery-detail-hero delivery-detail-hero--${statusTone}`}>
        <div className="delivery-detail-hero__main">
          <span className={`delivery-status-pill delivery-status-pill--${statusTone}`}>
            {statusLabel}
          </span>
          <span className="delivery-detail-hero__type">{deliveryTypeLabel(row.fulfillment_type)}</span>
          {vehicleLabel ? <span className="delivery-detail-hero__meta">{vehicleLabel}</span> : null}
          {priorityFeeDisplay ? (
            <span className="delivery-detail-hero__meta">
              Priority fee{' '}
              {formatDeliveryMoney(Number(priorityFeeDisplay), row.reference_fee_currency)}
            </span>
          ) : null}
        </div>
        <div className="delivery-detail-hero__fees">
          <div>
            <span className="delivery-detail-hero__fee-label">Reference</span>
            <strong>
              {formatDeliveryMoney(row.reference_fee_amount, row.reference_fee_currency)}
            </strong>
          </div>
          {isLalamove ? (
            <div>
              <span className="delivery-detail-hero__fee-label">Actual</span>
              <strong>
                {formatDeliveryMoney(actualFee, row.reference_fee_currency)}
              </strong>
            </div>
          ) : null}
        </div>
      </div>

      {isLalamove ? (
        <div className="delivery-detail-actions">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() =>
              void runAction('Status refreshed.', async () => {
                await refreshLalamoveDeliveryStatus(row.id);
              })
            }
          >
            Refresh
          </button>
          {(live?.trackingUrl || row.tracking_url) ? (
            <a
              className="btn-secondary btn-sm"
              href={live?.trackingUrl || row.tracking_url || '#'}
              target="_blank"
              rel="noreferrer"
            >
              Open tracking
            </a>
          ) : null}
          {canAct ? (
            <>
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                onClick={() => {
                  setPriorityFeeInput(
                    priorityFeeDisplay && Number(priorityFeeDisplay) > 0
                      ? String(priorityFeeDisplay)
                      : '5',
                  );
                  setPriorityFeeModalOpen(true);
                }}
              >
                Add priority fee
              </button>
              {hasDriver ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={busy}
                  onClick={() => {
                    if (
                      !window.confirm(
                        'Change driver? Available after ~15 minutes of matching, before pickup.',
                      )
                    ) {
                      return;
                    }
                    void runAction('Driver change requested.', async () => {
                      await changeLalamoveDriver(row.id, 'DRIVER_UNRESPONSIVE');
                    });
                  }}
                >
                  Change driver
                </button>
              ) : null}
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm('Cancel this Lalamove order?')) return;
                  void runAction('Order cancelled.', async () => {
                    await cancelLalamoveDelivery(row.id);
                  });
                }}
              >
                Cancel order
              </button>
            </>
          ) : null}
          {canReorder ? (
            <Link to={reorderPath} className="btn-primary btn-sm">
              Reorder
            </Link>
          ) : null}
          <p className="delivery-detail-actions__note">
            Edit dropoff / optimise route are only available via Lalamove quotation rules (edit once
            before pickup; route optimise at quote time). POD is requested on new orders.
          </p>
        </div>
      ) : (
        <div className="delivery-detail-actions">
          {!isDeliveryStatusTerminal(row.status) ? (
            <>
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                onClick={() => void updateInternalDeliveryStatus(row.id, 'completed').then(() => load())}
              >
                Mark completed
              </button>
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                onClick={() => void updateInternalDeliveryStatus(row.id, 'cancelled').then(() => load())}
              >
                Cancel
              </button>
            </>
          ) : null}
          {canReorder ? (
            <Link to={reorderPath} className="btn-primary btn-sm">
              Reorder
            </Link>
          ) : null}
        </div>
      )}

      <div className="delivery-detail-shell delivery-detail-shell--stacked">
        <aside className="delivery-detail-shell__map card">
          <div className="delivery-detail-panel__head">
            <h2 className="delivery-detail-panel__title">Tracking map</h2>
          </div>
          <DeliveryRouteMap
            stops={mapStops}
            driverLocation={driverLocation}
            showCoordinates={false}
            tall
            allowZoom={false}
          />
        </aside>

        <div className="delivery-detail-shell__body">
          <div className="delivery-detail-shell__left">
            {isLalamove ? (
              <section className="card delivery-detail-panel">
                <h2 className="delivery-detail-panel__title">Driver</h2>
                {driver ? (
                  <div className="delivery-driver">
                    {driver.photo ? (
                      <img
                        className="delivery-driver__photo"
                        src={driver.photo}
                        alt={driver.name || 'Driver'}
                      />
                    ) : (
                      <div className="delivery-driver__photo delivery-driver__photo--placeholder">
                        D
                      </div>
                    )}
                    <dl className="delivery-detail-facts delivery-detail-facts--compact">
                      <div>
                        <dt>Name</dt>
                        <dd>{driver.name || '—'}</dd>
                      </div>
                      <div>
                        <dt>Phone</dt>
                        <dd>{driver.phone || '—'}</dd>
                      </div>
                      <div>
                        <dt>Plate</dt>
                        <dd>{driver.plateNumber || '—'}</dd>
                      </div>
                      <div>
                        <dt>Updated</dt>
                        <dd>
                          {driver.updatedAt
                            ? formatSingaporeDateTime(driver.updatedAt)
                            : '—'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : (
                  <p className="delivery-form-card__hint" style={{ margin: 0 }}>
                    {hasDriver
                      ? 'Driver assigned — location/details not available yet (Lalamove window).'
                      : 'Waiting for a driver to accept the order.'}
                  </p>
                )}
              </section>
            ) : null}

            <section className="card delivery-detail-panel">
              <h2 className="delivery-detail-panel__title">Summary</h2>
              <dl className="delivery-detail-facts delivery-detail-facts--compact">
                <div>
                  <dt>Assignee</dt>
                  <dd>{deliveryAssigneeLabel(row)}</dd>
                </div>
                <div>
                  <dt>Creator</dt>
                  <dd>{row.created_by}</dd>
                </div>
                <div>
                  <dt>Requested by</dt>
                  <dd>{row.requested_by?.trim() || '—'}</dd>
                </div>
                <div>
                  <dt>Issue caused by</dt>
                  <dd>{row.issue_caused_by?.trim() || '—'}</dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatSingaporeDateTime(row.created_at)}</dd>
                </div>
                {isLalamove ? (
                  <div>
                    <dt>Lalamove order</dt>
                    <dd className="delivery-detail-facts__mono">
                      {row.lalamove_order_id || '—'}
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>Notes to driver</dt>
                  <dd>
                    {driverNotesEntries.length === 0 ? (
                      '—'
                    ) : driverNotesEntries.length === 1 && !multiStop ? (
                      driverNotesEntries[0]!.notes
                    ) : (
                      <ul className="delivery-detail-accounts">
                        {driverNotesEntries.map((entry) => (
                          <li key={`notes-${entry.sequence}`}>
                            {multiStop ? (
                              <strong>Dropoff {entry.sequence}</strong>
                            ) : null}
                            <span
                              className={
                                multiStop ? 'delivery-detail-facts__sub' : undefined
                              }
                              style={multiStop ? undefined : { whiteSpace: 'pre-wrap' }}
                            >
                              {entry.notes}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="card delivery-detail-panel">
              <h2 className="delivery-detail-panel__title">Pickup</h2>
              <dl className="delivery-detail-facts delivery-detail-facts--compact">
                <div>
                  <dt>Name</dt>
                  <dd>{row.pickup_json.name || '—'}</dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{row.pickup_json.phone || '—'}</dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>
                    {composeDeliveryAddress(row.pickup_json) || row.pickup_json.address || '—'}
                  </dd>
                </div>
              </dl>
            </section>
          </div>

          <div className="delivery-detail-shell__right">
            {batchRows.map((stopRow) => {
              const demAccountCode = stopRow.dem_customer_code?.trim() || '';
              const crmLinked = Boolean(stopRow.client_id);
              const crmName = crmLinked ? stopRow.client_name?.trim() || '' : '';
              const crmCode = crmLinked ? stopRow.client_code?.trim() || '' : '';
              // live.stops[0] = pickup; live.stops[stop_sequence] = this dropoff
              const podStop = liveStops[stopRow.stop_sequence] ?? null;
              const pod = podStop?.pod ?? null;

              return (
                <section key={stopRow.id} className="card delivery-detail-panel">
                  <h2 className="delivery-detail-panel__title">
                    {multiStop ? `Dropoff ${stopRow.stop_sequence}` : 'Dropoff'}
                  </h2>
                  <dl className="delivery-detail-facts delivery-detail-facts--compact">
                    <div>
                      <dt>DEM account</dt>
                      <dd>
                        <strong>{demAccountCode || '—'}</strong>
                      </dd>
                    </div>
                    <div>
                      <dt>CRM account</dt>
                      <dd>
                        {crmLinked ? (
                          <>
                            <strong>{crmName || crmCode || '—'}</strong>
                            {crmCode && crmCode !== crmName ? (
                              <span className="delivery-detail-facts__sub">{crmCode}</span>
                            ) : null}
                          </>
                        ) : (
                          <span className="delivery-form-card__hint" style={{ margin: 0 }}>
                            Not linked to CRM
                            {demAccountCode
                              ? ` (no match for ${demMainCustomerCode(demAccountCode) || demAccountCode})`
                              : ''}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Reason</dt>
                      <dd>{stopRow.reason || '—'}</dd>
                    </div>
                    <div>
                      <dt>Document no.</dt>
                      <dd>{stopRow.document_no?.trim() || '—'}</dd>
                    </div>
                    <div>
                      <dt>Name</dt>
                      <dd>{stopRow.dropoff_json.name || '—'}</dd>
                    </div>
                    <div>
                      <dt>Phone</dt>
                      <dd>{stopRow.dropoff_json.phone || '—'}</dd>
                    </div>
                    <div>
                      <dt>Address</dt>
                      <dd>
                        {composeDeliveryAddress(stopRow.dropoff_json) ||
                          stopRow.dropoff_json.address ||
                          '—'}
                      </dd>
                    </div>
                  </dl>

                  <div className="delivery-detail-pod-block">
                    <h3 className="delivery-detail-panel__subtitle">Proof of delivery</h3>
                    {pod ? (
                      <div className="delivery-pod-card">
                        <div className="delivery-pod-card__meta">
                          <strong>{pod.status || 'POD'}</strong>
                          {pod.signedAt ? (
                            <span>{formatSingaporeDateTime(pod.signedAt)}</span>
                          ) : null}
                        </div>
                        {pod.image ? (
                          <a href={pod.image} target="_blank" rel="noreferrer">
                            <img
                              src={pod.image}
                              alt={`POD dropoff ${stopRow.stop_sequence}`}
                            />
                          </a>
                        ) : (
                          <p className="delivery-form-card__hint" style={{ margin: 0 }}>
                            No image yet.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="delivery-form-card__hint" style={{ margin: 0 }}>
                        {isLalamove
                          ? 'POD appears here after this stop is delivered.'
                          : 'Not applicable for internal deliveries.'}
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>

      <Modal
        title="Add priority fee"
        open={priorityFeeModalOpen}
        onClose={() => {
          if (!busy) setPriorityFeeModalOpen(false);
        }}
      >
        <p className="delivery-form-card__hint" style={{ marginTop: 0 }}>
          Enter the priority fee amount (SGD). This replaces any previous tip on the order — it must
          be higher than the current priority fee.
        </p>
        <label className="field">
          <span className="field-label">Amount (SGD)</span>
          <input
            className="field-input"
            inputMode="decimal"
            autoFocus
            value={priorityFeeInput}
            onChange={(e) => setPriorityFeeInput(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="e.g. 5"
          />
        </label>
        <div className="modal-actions" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => setPriorityFeeModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !priorityFeeInput || Number(priorityFeeInput) <= 0}
            onClick={() => {
              void runAction('Priority fee updated.', async () => {
                const updated = await addLalamovePriorityFee(row.id, Number(priorityFeeInput));
                setLive(updated);
                setPriorityFeeModalOpen(false);
              });
            }}
          >
            {busy ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
